import { ICON_COLOR } from '@zep-us/zus/tokens';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useViewport } from '@/hooks';
import { CheckSmallCircleIcon } from '@/icons';
import { cn } from '@/utils';
import { type AiBuilderPageSurfaceMode } from '@/views/build/features/ai-builder-chat/components/AiBuilderShellTransition';
import { useBuildFormContext } from '../form/schema';
import { BuildHeaderMapTemplateButton } from './BuildHeaderMapTemplateButton';
import { BuildHeaderNextButton } from './BuildHeaderNextButton';
import { BuildHeaderSubmitButton } from './BuildHeaderSubmitButton';
import { BuildHeaderTitleInput } from './BuildHeaderTitleInput';
import { BuildStepToggleTab } from './BuildStepToggleTab';
import { ContinueOnPersistBuildButton } from './ContinueOnPersistBuildButton';
import { RouteBackButton } from './RouteBackButton';
import { UserChip } from './UserChip';
export type BuildStep = 'quiz' | 'settings';
type BuildHeaderProps = {
  step: BuildStep;
  onStepChange: (step: BuildStep) => void;
  onSubmitQuiz: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  isSubmitButtonShown: boolean;
  isBuildPageBodyInView: boolean;
  surfaceMode?: AiBuilderPageSurfaceMode;
};
export const BuildHeader = ({
  step,
  onStepChange,
  isSubmitButtonShown,
  onSubmitQuiz,
  onSubmittingChange,
  isBuildPageBodyInView,
  surfaceMode = 'page'
}: BuildHeaderProps) => {
  const {
    getValues
  } = useBuildFormContext();
  const {
    isPortable
  } = useViewport();
  const {
    t
  } = useTranslation(['build', 'common']);
  const usesAiShellLayout = surfaceMode !== 'page';
  const shouldHideShellControls = surfaceMode !== 'page';
  return <div className={cn('h-auto bg-white', usesAiShellLayout ? 'relative z-0 flex-none' : 'fixed left-0 right-0 top-0 z-50 transition-all duration-200 ease-in-out')} data-sentry-component="BuildHeader" data-sentry-source-file="BuildHeader.tsx">
      {/* Header container */}
      <div className="flex flex-col">
        {/* Main Header */}
        <div className={cn('flex items-center gap-x-sm px-md border-b border-alternative', usesAiShellLayout ? 'h-[64px] justify-start' : 'flex-wrap justify-between tablet-lg:grid tablet-lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] tablet-lg:gap-sm tablet-lg:h-[64px]')}>
          {/* Left section */}
          <div className={cn('flex h-[64px] min-w-0 flex-1 items-center gap-xs overflow-hidden', usesAiShellLayout && 'pl-2xs')}>
            {/* AI 채팅 셸에서는 좌측 뒤로가기 아이콘 버튼을 숨긴다 (시안 39095:110633) */}
            {!usesAiShellLayout && <RouteBackButton />}
            <BuildHeaderMapTemplateButton data-sentry-element="BuildHeaderMapTemplateButton" data-sentry-source-file="BuildHeader.tsx" />
            <BuildHeaderTitleInput data-sentry-element="BuildHeaderTitleInput" data-sentry-source-file="BuildHeader.tsx" />
            <UserChip data-sentry-element="UserChip" data-sentry-source-file="BuildHeader.tsx" />
          </div>

          {!shouldHideShellControls && <AnimatePresence>
              <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: !isPortable || isBuildPageBodyInView ? '62px' : 0,
            opacity: !isPortable || isBuildPageBodyInView ? 1 : 0,
            willChange: 'height, opacity'
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className={cn('w-full flex items-center justify-center pb-sm pt-2xs order-3', 'tablet-lg:w-auto tablet-lg:py-0 tablet-lg:order-2 tablet-lg:border-0', isPortable && !isBuildPageBodyInView && 'p-0')}>
                <BuildStepToggleTab currentStep={step} onBuildStepChange={onStepChange} />
              </motion.div>
            </AnimatePresence>}

          {/* Right section */}
          {!shouldHideShellControls && <div className="order-2 flex h-[64px] items-center gap-xs tablet-lg:order-3 tablet-lg:flex-1 tablet-lg:justify-end">
              <div className={'mr-xs hidden shrink-0 items-center text-[13px] font-semibold leading-normal text-assistive mobile-lg:flex'}>
                <CheckSmallCircleIcon size={'xs'} fill={ICON_COLOR.positive} className="mr-2xs" />
                {t('common:saving')}
                <div className="ml-sm h-[18px] w-[1px] border-l border-l-alternative" />
              </div>
              <ContinueOnPersistBuildButton />
              {isSubmitButtonShown ? <BuildHeaderSubmitButton onSubmitQuiz={onSubmitQuiz} onSubmittingChange={onSubmittingChange} /> : <BuildHeaderNextButton onClick={() => {
            logger.track('click_next_step_button', {
              ...getValues(),
              quizList: getValues('quizList')?.length || 0,
              materials: getValues('materials')?.length || 0
            });
            onStepChange('settings');
          }} />}
            </div>}
        </div>
      </div>
    </div>;
};